import {
	auditService,
	GET_CASE_STEPS_STATUS,
	GET_LANDING_PAGE_CASE_CLAIM_FORM,
	GET_LANDING_PAGE_CASE_QUESTION_TYPE,
	GET_LAWYER_CASE_DETAILS,
} from '@/api';
import { caseApplicantService } from '@/api/caseApplicant';
import { LoaderScreen, Typography } from '@/components/common';
import { ButtonsForm, LandingQuestion } from '@/components/dashboard';
import { InputList } from '@/components/form';
import { FileYourClaimScreen } from '@/components/screens/cases';
import { autosaveTimingClaimForm, errorFieldMessage } from '@/const';
import { CaseContext } from '@/context/CaseContext';
import { useNotification } from '@/hooks';
import useModalPreviewPage from '@/hooks/useModalPreviewPage';
import {
	ApprovePhase,
	CaseDetailsLawyer,
	LandingPageCaseClaimForm as LandingPageCaseClaimFormType,
	LandingPageCaseClaimFormPayload,
	Option,
	QuestionType,
	StepStatusCase,
} from '@/interfaces';
import { getQuestionIcon, getTitleFromQuestionType } from '@/lib';
import { Transition } from '@headlessui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import { getValidCaseId } from '@/utils';

interface LandingPageCaseClaimFormI {
	phaseId: number;
	stepCase?: StepStatusCase;
	caseDetails: CaseDetailsLawyer | undefined;
	onQuestionsChange?: (questions: LandingPageCaseClaimFormPayload[]) => void;
}
export interface MultipleAnswerErrorI {
	index: number; // question index
	errors: {
		answerIndex: number;
		hasError: boolean;
	}[];
}
export const LandingPageCaseClaimForm: React.FC<LandingPageCaseClaimFormI> = ({
	phaseId,
	stepCase,
	caseDetails,
	onQuestionsChange,
}) => {
	const router = useRouter();
	const caseId = getValidCaseId(router.query.id);
	const id = caseId;
	const { isCaseReadOnly, setSaveStatus } = React.useContext(CaseContext);
	const isStepCompleted = stepCase?.status === 'completed';
	const isStepApproved = stepCase?.status === 'approved';
	const queryClient = useQueryClient();
	const { toastError } = useNotification();
	const [showMultipleAnswersError, setShowMultipleAnswersError] =
		React.useState<boolean>(false);
	const [questionList, setQuestionList] = React.useState<Option[]>();
	const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
	const [isFormHydrated, setIsFormHydrated] = React.useState(false);
	const [isLoadingAutosaved, setIsLoadingAutosaved] = React.useState(false);

	const lastSavedData = React.useRef<LandingPageCaseClaimFormType[]>([]);

	const { ModalPreviewPage, hidePreviewPage, showPreviewPage } =
		useModalPreviewPage();

	// form
	const {
		register,
		getValues,
		control,
		setValue,
		formState: { errors },
		clearErrors,
		handleSubmit,
		reset,
	} = useForm({
		mode: 'onChange',
	});

	const { fields, remove, append, swap } = useFieldArray({
		name: 'questions',
		control,
	});

	const formValues = useWatch({
		control,
	});
	const rules = {
		add_question: {
			validate: () => fields.length > 0 || errorFieldMessage,
		},
	};
	const {
		data: landingPageCaseClaimForm,
		isLoading: isLoadingClaimForm,
		refetch,
	} = useQuery({
		queryKey: [GET_LANDING_PAGE_CASE_CLAIM_FORM],
		queryFn: () =>
			caseApplicantService.getLandingPageCaseClaimForm(id as string),
		enabled: !!id,
		refetchOnWindowFocus: false,
	});

	const {
		data: landingPageCaseQuestionType,
		error,
		isLoading,
	} = useQuery({
		queryKey: [GET_LANDING_PAGE_CASE_QUESTION_TYPE],
		queryFn: () => caseApplicantService.getLandingPageCaseQuestionsTypes(),
	});

	React.useEffect(() => {
		if (landingPageCaseQuestionType) {
			const auxQuestionList: Option[] = [
				{
					label: 'Select',
					name: '',
					placeholder: true,
				},
			];
			landingPageCaseQuestionType.data.forEach((item) =>
				auxQuestionList.push({
					label: getTitleFromQuestionType(item.value),
					name: item.value,
					icon: getQuestionIcon(item.value),
				})
			);

			setQuestionList(auxQuestionList);
		}
	}, [landingPageCaseQuestionType]);

	React.useEffect(() => {
		if (landingPageCaseClaimForm) {
			remove();
			landingPageCaseClaimForm.data.map((item) =>
				append({
					key: item.id,
					question_type: item.question_type,
					question: item.question,
					expected_answer: item.expected_answer,
				})
			);
		}
	}, [landingPageCaseClaimForm]);

	const isFormFilled = React.useMemo(() => {
		if (
			!Array.isArray(formValues.questions) ||
			formValues.questions.length === 0
		) {
			return false;
		}

		return formValues.questions.every(
			(item: LandingPageCaseClaimFormPayload) => {
				if (!item.question || !item.question_type) return false;

				if (item.question_type === 'MULTIPLE') {
					if (!Array.isArray(item.expected_answers)) return false;

					const hasAtLeastOneChecked = item.expected_answers.some(
						(opt: any) => opt?.isCorrect
					);

					return hasAtLeastOneChecked;
				} else {
					return (
						!!item.question &&
						!!item.question_type &&
						(item.question_type === 'TEXT' || !!item.expected_answer)
					);
				}
			}
		);
	}, [formValues.questions]);

	const multipleChoiceErrors = React.useMemo(() => {
		const questions = formValues.questions;
		if (!Array.isArray(questions)) return [];

		return questions.reduce(
			(
				acc: {
					index: number;
					errors: { answerIndex: number; hasError: boolean }[];
				}[],
				question: any,
				qIndex: number
			) => {
				if (
					question.question_type === 'MULTIPLE' &&
					Array.isArray(question.expected_answers)
				) {
					const expectedAnswers = question.expected_answers;

					const anyHasDetail = expectedAnswers.some((ans: any) =>
						ans?.detail?.trim()
					);

					const anyHasValidValue = expectedAnswers.some(
						(ans: any) => ans?.value && ans.value !== 'none'
					);

					const errors = expectedAnswers
						.map((answer: any, aIndex: number) => {
							const value = answer?.value;
							const detail = answer?.detail?.trim() ?? '';
							let hasError = false;

							// Rule 1: If value is missing or 'none', detail is required
							if (!value || value === 'none') {
								if (!detail) {
									hasError = true;
								}
							}

							// Rule 2: If any answer has a detail, all must
							if (anyHasDetail && !detail) {
								hasError = true;
							}

							// Rule 3: If any answer has a valid value, all must
							if (anyHasValidValue && (!value || value === 'none')) {
								hasError = true;
							}

							return { answerIndex: aIndex, hasError };
						})
						.filter((e: any) => e.hasError);

					if (errors.length > 0) {
						acc.push({ index: qIndex, errors });
					}
				}
				return acc;
			},
			[]
		);
	}, [formValues.questions]);

	React.useEffect(() => {
		if (onQuestionsChange && Array.isArray(formValues.questions)) {
			onQuestionsChange(
				formValues.questions as LandingPageCaseClaimFormPayload[]
			);
		}
	}, [formValues.questions]);

	React.useEffect(() => {
		if (landingPageCaseClaimForm?.data) {
			const mapped = mapBackendToFormData(landingPageCaseClaimForm.data);
			reset(mapped); // from react-hook-form
			setIsFormHydrated(true); // optional dirty-check guard
			lastSavedData.current = landingPageCaseClaimForm.data;
		}
	}, [landingPageCaseClaimForm]);

	React.useEffect(() => {
		if (!landingPageCaseClaimForm?.data || !formValues.questions) return;

		clearErrors();
		if (getInfoIsEdited()) {
			if (isFormFilled && multipleChoiceErrors.length === 0) {
				debouncedFunction('complete');
			}

			if (!isFormFilled && caseDetails?.is_draft) {
				debouncedFunction('save');
			}
		}
	}, [
		formValues,
		landingPageCaseClaimForm,
		isFormFilled,
		multipleChoiceErrors,
	]);

	React.useEffect(() => {
		setSaveStatus({ isSaving: isLoadingAutosaved });
	}, [isLoadingAutosaved, setSaveStatus]);

	const debouncedFunction = useDebouncedCallback(
		(type: 'save' | 'complete') => {
			setShowMultipleAnswersError(false);

			if (type === 'complete' && multipleChoiceErrors.length === 0) {
				handleComplete();
			} else {
				handleSave();
			}
		},
		autosaveTimingClaimForm
	);

	// const debouncedToast = useDebouncedCallback(() => {
	// 	toastInfo('Your case details have been saved.', 'bottom-center', Slide);
	// }, 5000); //5seg

	const mapBackendToFormData = (
		dataFromBackend: LandingPageCaseClaimFormType[]
	) => {
		return {
			questions: dataFromBackend.map((item) => {
				const base = {
					key: item.id,
					question_type: item.question_type,
					question: item.question,
					expected_answer: item.expected_answer,
				};

				if (item.question_type === 'MULTIPLE') {
					return {
						...base,
						expected_answers: item.multiple_choise_questions.map((option) => ({
							value: option.brand_id,
							detail: option.complement,
							isCorrect: option.is_correct,
						})),
					};
				}

				return base;
			}),
			add_question: '', // default blank input
		};
	};

	const getInfoIsEdited = (): boolean => {
		if (!isFormHydrated) return false;

		const dataSaved = lastSavedData.current;
		const dataUpdated = getValues().questions || [];
		// const dataSaved = landingPageCaseClaimForm?.data || [];
		// const dataUpdated = formValues?.questions || [] replace due to asyn conflicts

		const isEdited = dataSaved.some((item, index) => {
			const baseChanged =
				item.expected_answer !== dataUpdated[index]?.expected_answer ||
				item.question_type !== dataUpdated[index]?.question_type ||
				item.question !== dataUpdated[index]?.question;

			if (baseChanged) return true;

			if (item.question_type === 'MULTIPLE') {
				const savedAnswers = item.multiple_choise_questions || [];
				const currentAnswers = dataUpdated[index]?.expected_answers || [];

				if (savedAnswers.length !== currentAnswers.length) return true;

				return savedAnswers.some((savedOpt: any, i: number) => {
					const currentOpt = currentAnswers[i];

					const isBrandIdEqual =
						(savedOpt?.brand_id === null && currentOpt?.value === 'none') ||
						savedOpt?.brand_id === currentOpt?.value;

					return (
						!isBrandIdEqual ||
						savedOpt?.complement !== currentOpt?.detail ||
						savedOpt?.is_correct !== currentOpt?.isCorrect
					);
				});
			}

			return false;
		});

		return dataSaved.length !== dataUpdated.length || isEdited;
	};

	const getDataToSave = () => {
		const data = getValues().questions as any[];
		const newData: LandingPageCaseClaimFormPayload[] = [];
		data?.forEach((item: LandingPageCaseClaimFormPayload, idx) => {
			newData.push({
				question: item.question || '',
				expected_answer:
					item.question_type === 'DATE'
						? item.expected_answer?.replace(/\s+/g, '') || null //remove blank spaces
						: item.expected_answer || null,
				question_type: item.question_type,
				numerical_order: idx,
				multiple_responses:
					item.question_type === 'MULTIPLE'
						? item.expected_answers?.map((answer, index) => ({
								complement: answer.detail,
								brand_id: answer.value === 'none' ? null : answer.value || null,
								is_correct: answer.isCorrect,
								numerical_order: index,
						  }))
						: [],
			});
		});
		return newData;
	};

	const handleSave = async () => {
		setIsLoadingAutosaved(true);
		try {
			const newData = getDataToSave();

			// prevents multiple questions with all fields empty
			if (
				newData.some(
					(item) =>
						!item.question &&
						!item.expected_answer &&
						item.multiple_responses?.every(
							(respose) => !respose.brand_id && !respose.complement
						)
				)
			) {
				return;
			}

			const res = await caseApplicantService.postLandingPageCaseClaimForm(
				id as string,
				newData
			);
			lastSavedData.current = res.data; // update with the data saved
			if (caseId) {
				queryClient.refetchQueries({
					queryKey: [GET_CASE_STEPS_STATUS, caseId],
				});
			}
			// refetch(); // hide due to have conflict with the auto save
			// debouncedToast();
		} catch (error) {
			toastError('An error occurred while saving. Please try again.');
		} finally {
			setIsLoadingAutosaved(false);
		}
	};

	const handleComplete = async () => {
		setSaveStatus({ isError: false });
		setIsLoadingAutosaved(true);
		try {
			const newData = getDataToSave();

			const res = await caseApplicantService.putLandingPageCaseClaimForm(
				id as string,
				newData
			);
			// refetch(); // hide due to have conflict with the auto save
			lastSavedData.current = res.data; // update with the data saved
			handleApprove();
			setSaveStatus({ isError: false });
			// queryClient.refetchQueries({ queryKey: [GET_CASE_STEPS_STATUS] });
			// debouncedToast();
		} catch (error) {
			setSaveStatus({ isError: true });
			toastError('An error occurred while completing.. Please try again.');
		} finally {
			setIsLoadingAutosaved(false);
		}
	};

	const handleApprove = async () => {
		setSaveStatus({ isError: false });
		setIsLoadingAutosaved(true);
		try {
			// debouncedToast.cancel(); //  Suppress save toast on approve action
			if (id && stepCase) {
				const data: ApprovePhase = {
					case_id: id as string,
					lawyer_stepper_id: phaseId,
					stepper_form_id: stepCase.step_id,
				};
				await auditService.postApprovePhase(data);
				// toastSuccess('The claim form has been successfully approved.');
				hidePreviewPage();
				refetch();
				if (caseId) {
					queryClient.refetchQueries({
						queryKey: [GET_CASE_STEPS_STATUS, caseId],
					});
					queryClient.refetchQueries({
						queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
					});
				}
				setSaveStatus({ isError: false });
			}
		} catch (error) {
			setSaveStatus({ isError: true });
			toastError('An error occurred while completing, please try again.');
		} finally {
			setIsLoadingAutosaved(false);
		}
	};

	const handlePreview = () => {
		if (
			(!isStepCompleted || multipleChoiceErrors.length > 0) &&
			!isStepApproved
		)
			return;
		refetch().then(() => {
			showPreviewPage();
		});
	};

	const handleAddQuestion = (questionType: QuestionType) => {
		questionType &&
			append({
				question_type: questionType,
				question: '',
				expected_answer: '',
			});
	};

	return (
		<div className="w-full">
			{error && (
				<Typography type="semibold-16" className="text-center mb-10">
					Error getting the questions
				</Typography>
			)}
			{(isLoading || isLoadingClaimForm) && <LoaderScreen />}
			<form className="flex flex-col gap-y-8">
				<ul className="space-y-10">
					{fields.map((item, index) => {
						const question = item as any;
						return (
							<li key={'question-claim-form-' + index}>
								<Transition
									key={item.id}
									show={true}
									appear={true}
									enter="transition duration-100 ease-out"
									enterFrom="transform scale-95 opacity-0"
									enterTo="transform scale-100 opacity-100"
									leave="transition duration-75 ease-out"
									leaveFrom="transform scale-100 opacity-100"
									leaveTo="transform scale-95 opacity-0"
								>
									<LandingQuestion
										remove={remove}
										swap={swap}
										getValues={getValues}
										setValue={setValue}
										register={register}
										control={control}
										errors={errors}
										questionType={question.question_type}
										index={index}
										clearErrors={clearErrors}
										totalQuestions={fields.length}
										isCaseReadOnly={isCaseReadOnly}
										showMultipleAnswersError={showMultipleAnswersError}
										multipleAnswersError={multipleChoiceErrors}
										multipleChoiceQuestion={
											landingPageCaseClaimForm?.data[index]
												?.multiple_choise_questions
										}
										caseDetails={caseDetails}
									/>
								</Transition>
							</li>
						);
					})}
				</ul>
				<div
					className={clsx('flex flex-col', {
						'gap-y-40': isDropdownOpen,
					})}
				>
					{questionList && (
						<InputList
							disabled={isCaseReadOnly}
							name="add_question"
							title="Add a Question"
							placeholder="Select"
							register={register}
							className={clsx('w-[410px] mb-8')}
							classNameTitle="font-semibold"
							options={questionList}
							setValueInput={setValue}
							handleChange={(value) => {
								handleAddQuestion(value as QuestionType);
								clearErrors('add_question');
							}}
							resetValueAfterSelecting={true}
							onToggleOpen={setIsDropdownOpen}
							rules={rules.add_question}
							error={errors.add_question}
						/>
					)}

					<ButtonsForm
						onClickPreview={() => {
							if (multipleChoiceErrors.length > 0 || !isStepCompleted) {
								setShowMultipleAnswersError(true);
							}
							handleSubmit(
								() => {
									handlePreview();
								},
								(errors) => {
									console.log('Validation errors', errors);
								}
							)();
						}}
						completeButtonClassName={
							(!isFormFilled ||
								!isStepCompleted ||
								multipleChoiceErrors.length > 0) &&
							!isStepApproved
								? 'opacity-60 cursor-default'
								: ''
						}
						onlyOneButton
						isApproved={isStepApproved}
						buttonTitle="Preview Claim Form"
					/>
				</div>
			</form>

			<ModalPreviewPage
				handleClickApprove={handleApprove}
				handleClickBack={hidePreviewPage}
				handleClickEdit={hidePreviewPage}
				backCustomLabel="Back to edit"
				title={
					isStepApproved || isCaseReadOnly
						? 'This is your preview page.'
						: `This is your preview page. Go back to make changes or 'Approve' to stage it for going live.`
				}
				hideApproveButton={isStepApproved || isCaseReadOnly}
				hideEditButton={true}
				classNameContainerChildren="bg-[#F4F4F9]"
				classNameCardContainer="overflow-y-auto"
			>
				<div className="max-w-[90%] mx-auto">
					<FileYourClaimScreen
						id={id as string}
						previewMode={true}
						previewQuestions={landingPageCaseClaimForm?.data ?? []}
						status="Open to new clients"
					/>
				</div>
			</ModalPreviewPage>
		</div>
	);
};
